
import React, { useEffect, useRef, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { UserData } from '@/lib/profile/models/user.model';
import { showDevError } from '@/components/ui/dev-error-toast';
import { AuthContext, type UserRole, type ApprovalStatus } from '@/lib/auth/models/auth.model';
import i18n from '@/i18n';
import { DEFAULT_LANGUAGE, isAppLanguage } from '@/constants/languages';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [isApproved, setIsApproved] = useState(false);
  const [userStatus, setUserStatus] = useState<ApprovalStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoSmallUrl, setLogoSmallUrl] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const initializedForUserIdRef = useRef<string | null>(null);

  const initializeAuth = async (session: Session) => {
    if (!session?.user) return;

    try {
      console.log('🔄 [AuthContext] initializeAuth called for userId:', session.user.id);
      console.trace('[AuthContext] initializeAuth call stack');

      const { userData } = await fetchUserData(session.user.id);

      // Apply role chosen before Google OAuth redirect (stored in sessionStorage)
      const pendingJson = sessionStorage.getItem('p2b_google_signup');
      if (pendingJson) {
        sessionStorage.removeItem('p2b_google_signup');
        try {
          const { role: pendingRole, ts } = JSON.parse(pendingJson);
          const isRecent = Date.now() - ts < 10 * 60 * 1000; // 10 min window
          
          // Valida che il ruolo sia uno di quelli supportati
          const validRoles = ['traveler', 'travel_designer', 'travel_advisor_member', 'agency', 'admin'];
          if (isRecent && validRoles.includes(pendingRole)) {
            const updates: Record<string, string> = { role: pendingRole };
            
            // Imposta approval_status secondo le regole di business:
            // - traveler → approved
            // - travel_advisor_member → approved (verificato dall'agenzia)
            // - admin, agency, travel_designer → pending (richiedono approvazione Place2Be)
            if (pendingRole === 'traveler' || pendingRole === 'travel_advisor_member') {
              updates.approval_status = 'approved';
            } else {
              updates.approval_status = 'pending';
            }
            
            await supabase.from('users').update(updates).eq('id', session.user.id);
            userData.role = pendingRole;
            userData.approval_status = updates.approval_status as ApprovalStatus;
          }
        } catch {
          // malformed sessionStorage value — ignore
        }
      }

      setUserData(userData);
      setIsApproved(userData.approval_status === 'approved');
      setUserStatus((userData.approval_status as ApprovalStatus) ?? null);
      setUserRole(userData.role || 'traveler');

      // Set user's preferred language if available
      if (userData.preferred_language) {
        await i18n.changeLanguage(userData.preferred_language);
        localStorage.setItem('language', userData.preferred_language);
      }

    } catch (error) {
      console.error('Error in initial auth check:', error);
      showDevError({ error, context: 'initial auth check' });
      // Set default values on error
      setUserRole('traveler');
      setIsApproved(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserData = async (userId: string) => {

    console.log('🔄 Fetching user data for:', userId);

    // Load full profile (core + public_profile + private_data)
    const { data: profileData, error: profileError } = await supabase.rpc('get_my_full_profile' as any);
    
    if (profileError) {
      console.error('Error fetching full profile:', profileError);
      throw profileError;
    }

    // Merge core data with public_profile data
    const mergedData = {
      ...profileData?.core,
      ...(profileData?.public_profile || {}),
    };

    const userData = new UserData(mergedData);

    return { userData, role: userData.role };
  };

  useEffect(() => {

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        const userId = session?.user?.id ?? null;
        console.log('[AuthContext] onAuthStateChange event:', event, '| userId:', userId);

        // Supabase fires both SIGNED_IN and INITIAL_SESSION on page load — deduplicate
        if (userId && userId === initializedForUserIdRef.current) {
          return;
        }
        if (userId) {
          initializedForUserIdRef.current = userId;
        }

        setTimeout(async () => {

          setSession(session);
          setUser(session?.user ?? null);

          await initializeAuth(session);

          setLoading(false);
        }, 0);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, metadata: Record<string, unknown>) => {
    // Determina il ruolo basato sui metadati
    const role: UserRole = (metadata.role as UserRole) || 'travel_designer';
    // La lingua in cui ha compilato il form: handle_new_user la scrive sul profilo,
    // e da lì il mailer sceglie la variante _it/_en delle mail (es. approve_new_agency).
    const uiLanguage = i18n.resolvedLanguage?.slice(0, 2);
    const preferred_language = isAppLanguage(uiLanguage) ? uiLanguage : DEFAULT_LANGUAGE;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          ...metadata,
          role,
          preferred_language
        }
      }
    });

    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (!error) {
      setLoading(true);
    }

    return { error };
  };

  useEffect(() => {
    if (!userData) {
      setLogoUrl(null);
      setLogoSmallUrl(null);
      // Don't touch logoLoading: auth not ready yet
      return;
    }
    // Il DB risolve logo proprio vs agenzia secondo la preferenza logo_source
    let cancelled = false;
    supabase.rpc('get_my_logo_settings' as any).then(({ data }) => {
      if (cancelled) return;
      const effective = (data as any)?.effective;
      setLogoUrl(effective?.logo_url || null);
      setLogoSmallUrl(effective?.logo_small_url || null);
      setLogoLoading(false);
    });
    return () => { cancelled = true; };
  }, [userData, userRole]);

  const refreshUserData = async () => {
    if (!user?.id) return;
    try {
      const { userData } = await fetchUserData(user.id);
      setUserData(userData);
      setIsApproved(userData.approval_status === 'approved');
      setUserStatus((userData.approval_status as ApprovalStatus) ?? null);
      setUserRole(userData.role || 'traveler');
      
      // Set user's preferred language if available
      if (userData.preferred_language) {
        await i18n.changeLanguage(userData.preferred_language);
        localStorage.setItem('language', userData.preferred_language);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  const signOut = async () => {
    try {
      setUserRole(null);
      setIsApproved(false);
      setUserStatus(null);
      setUser(null);
      setSession(null);
      setUserData(null);
      setLogoUrl(null);
      setLogoSmallUrl(null);
      setLogoLoading(true);
      initializedForUserIdRef.current = null;
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
      showDevError({ error, context: 'sign out' });
      throw error;
    }
  };

  const value = {
    user,
    userData,
    session,
    userRole,
    isApproved,
    userStatus,
    logoUrl,
    logoLoading,
    logoSmallUrl,
    signUp,
    signIn,
    signOut,
    refreshUserData,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
